import React from 'react';

const MINIMAP_WIDTH = 180;
const MINIMAP_HEIGHT = 120;
const MINIMAP_PADDING = 8;

export const BoardMapMiniMap = ({
  canvasWidth,
  canvasHeight,
  viewport,
  viewportWidth,
  viewportHeight,
  nodeRectById,
  nodeMetaById,
  offsetX = 0,
  offsetY = 0,
  setViewport,
}) => {
  const [collapsed, setCollapsed] = React.useState(false);
  const safeCanvasWidth = Math.max(1, Number(canvasWidth) || 1);
  const safeCanvasHeight = Math.max(1, Number(canvasHeight) || 1);
  const safeViewportWidth = Math.max(1, Number(viewportWidth) || 1);
  const safeViewportHeight = Math.max(1, Number(viewportHeight) || 1);
  const safeZoom = Math.max(Number(viewport?.zoom) || 1, 0.05);
  const contentWidth = MINIMAP_WIDTH - MINIMAP_PADDING * 2;
  const contentHeight = MINIMAP_HEIGHT - MINIMAP_PADDING * 2;
  const scale = Math.min(
    contentWidth / safeCanvasWidth,
    contentHeight / safeCanvasHeight,
  );
  const mapWidth = safeCanvasWidth * scale;
  const mapHeight = safeCanvasHeight * scale;
  const mapLeft = (MINIMAP_WIDTH - mapWidth) / 2;
  const mapTop = (MINIMAP_HEIGHT - mapHeight) / 2;
  const viewX = -((Number(viewport?.x) || 0) / safeZoom);
  const viewY = -((Number(viewport?.y) || 0) / safeZoom);
  const viewW = safeViewportWidth / safeZoom;
  const viewH = safeViewportHeight / safeZoom;

  const nodeRects = React.useMemo(() => {
    if (!nodeRectById || typeof nodeRectById.forEach !== 'function') {
      return [];
    }
    const rects = [];
    nodeRectById.forEach((rect, nodeId) => {
      const width = Math.max(2, Number(rect?.w) || 0);
      const height = Math.max(2, Number(rect?.h) || 0);
      const x = Number(rect?.cx) - width / 2 + offsetX;
      const y = Number(rect?.cy) - height / 2 + offsetY;
      if (!Number.isFinite(x) || !Number.isFinite(y)) {
        return;
      }
      rects.push({
        id: nodeId,
        x,
        y,
        w: width,
        h: height,
        meta: nodeMetaById?.get?.(nodeId) || {},
      });
    });
    return rects;
  }, [nodeRectById, nodeMetaById, offsetX, offsetY]);

  const renderNode = rect => {
    const type = rect?.meta?.type || 'node';
    const color = String(rect?.meta?.color || 'secondary').trim() || 'secondary';
    const fill = `rgb(var(--${color}))`;
    const minSize = 6 / scale;
    const x = rect.x;
    const y = rect.y;
    const width = Math.max(rect.w, minSize);
    const height = Math.max(rect.h, minSize);
    const cx = x + rect.w / 2;
    const cy = y + rect.h / 2;

    if (type === 'pin') {
      return (
        <circle
          key={rect.id}
          cx={cx}
          cy={cy}
          r={Math.max(width, height) / 2}
          fill={fill}
          opacity='0.9'
        />
      );
    }

    if (type === 'region') {
      return (
        <rect
          key={rect.id}
          x={x}
          y={y}
          width={width}
          height={height}
          fill={fill}
          fillOpacity='0.18'
          stroke={fill}
          strokeWidth={2 / scale}
          rx={2 / scale}
        />
      );
    }

    if (type === 'drawing') {
      return (
        <line
          key={rect.id}
          x1={x}
          y1={y}
          x2={x + width}
          y2={y + height}
          stroke={fill}
          strokeWidth={3 / scale}
          strokeLinecap='round'
          opacity='0.85'
        />
      );
    }

    if (type === 'group') {
      return (
        <rect
          key={rect.id}
          x={x}
          y={y}
          width={width}
          height={height}
          fill='transparent'
          stroke={fill}
          strokeWidth={2 / scale}
          strokeDasharray={`${6 / scale} ${4 / scale}`}
          rx={4 / scale}
          opacity='0.85'
        />
      );
    }

    if (type === 'widget') {
      return (
        <rect
          key={rect.id}
          x={x}
          y={y}
          width={width}
          height={height}
          fill='rgb(var(--contrast))'
          opacity='0.42'
          rx={3 / scale}
        />
      );
    }

    return (
      <rect
        key={rect.id}
        x={x}
        y={y}
        width={width}
        height={height}
        fill={fill}
        opacity='0.72'
        rx={rect?.meta?.shapeType === 'circle' ? Math.min(width, height) / 2 : 2 / scale}
      />
    );
  };

  const recenterFromEvent = React.useCallback(
    event => {
      event.preventDefault();
      event.stopPropagation();
      const rect = event.currentTarget.getBoundingClientRect();
      const localX = event.clientX - rect.left - mapLeft;
      const localY = event.clientY - rect.top - mapTop;
      const canvasX = Math.max(0, Math.min(safeCanvasWidth, localX / scale));
      const canvasY = Math.max(0, Math.min(safeCanvasHeight, localY / scale));
      setViewport(prev => ({
        ...prev,
        x: safeViewportWidth / 2 - canvasX * prev.zoom,
        y: safeViewportHeight / 2 - canvasY * prev.zoom,
      }));
    },
    [
      mapLeft,
      mapTop,
      safeCanvasHeight,
      safeCanvasWidth,
      safeViewportHeight,
      safeViewportWidth,
      scale,
      setViewport,
    ],
  );

  const handlePointerDown = React.useCallback(
    event => {
      recenterFromEvent(event);
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    [recenterFromEvent],
  );

  if (collapsed) {
    return (
      <button
        type='button'
        className='absolute bottom-0 left-1 z-9998 rounded border border-contrast border-opacity-20 bg-main bg-opacity-80 px-1 py-05 text-xs text-contrast shadow backdrop-blur-sm transition hover:opacity-80'
        data-board-wheel-lock='true'
        onPointerDown={event => event.stopPropagation()}
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
          setCollapsed(false);
        }}
        title='Afficher la minimap'
      >
        Minimap
      </button>
    );
  }

  return (
    <div
      className='absolute bottom-0 left-1 z-9998 rounded border border-contrast border-opacity-20 bg-main bg-opacity-80 p-05 shadow backdrop-blur-sm'
      data-board-wheel-lock='true'
      onPointerDown={handlePointerDown}
      onPointerMove={event => {
        if (event.buttons === 1) {
          recenterFromEvent(event);
        }
      }}
      onClick={event => event.stopPropagation()}
    >
      <button
        type='button'
        className='absolute right-05 top-05 z-10 rounded bg-main bg-opacity-80 px-05 py-px text-xs leading-4 text-contrast transition hover:opacity-80'
        onPointerDown={event => event.stopPropagation()}
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
          setCollapsed(true);
        }}
        title='Réduire la minimap'
      >
        −
      </button>
      <svg
        width={MINIMAP_WIDTH}
        height={MINIMAP_HEIGHT}
        viewBox={`0 0 ${MINIMAP_WIDTH} ${MINIMAP_HEIGHT}`}
        className='block cursor-crosshair'
        aria-label='Minimap du tableau blanc'
      >
        <rect
          x={mapLeft}
          y={mapTop}
          width={mapWidth}
          height={mapHeight}
          rx='4'
          fill='rgb(var(--main-d))'
          opacity='0.45'
        />
        <g transform={`translate(${mapLeft} ${mapTop}) scale(${scale})`}>
          {nodeRects.map(renderNode)}
          <rect
            x={viewX}
            y={viewY}
            width={viewW}
            height={viewH}
            fill='rgb(var(--contrast))'
            opacity='0.08'
            stroke='rgb(var(--contrast))'
            strokeWidth={2 / scale}
          />
        </g>
      </svg>
    </div>
  );
};
